import { NgStyle } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  computed,
  input,
  InputSignal,
  output,
  OutputEmitterRef,
  Signal,
  signal,
  ViewEncapsulation,
  WritableSignal
} from '@angular/core';
import { MatIconModule } from '@angular/material/icon';
import { TranslocoPipe } from '@jsverse/transloco';
import type { DataGridColumn } from '../data-grid.types';
import type {
  ColumnFilter,
  DateFilterOperator,
  NumericFilterOperator,
  TextFilterOperator
} from '../column-filter.types';

/** Horizontal padding from the viewport edge when clamping the popup. */
const POPUP_VIEWPORT_PADDING_PX: number = 8;

/** Minimum popup width in pixels. */
const POPUP_MIN_WIDTH_PX: number = 220;

/** Maximum popup width in pixels (must match `.dg-filter-menu { max-width }`). */
const POPUP_MAX_WIDTH_PX: number = 320;

/** Vertical gap between the anchor header and the popup. */
const POPUP_ANCHOR_GAP_PX: number = 4;

/** Ordered list of text filter operators with display labels. */
const TEXT_OPERATORS: ReadonlyArray<{ value: TextFilterOperator; label: string }> = [
  { value: 'contains', label: 'components.dataGrid.operators.contains' },
  { value: 'equals', label: 'components.dataGrid.operators.equals' },
  { value: 'startsWith', label: 'components.dataGrid.operators.startsWith' },
  { value: 'endsWith', label: 'components.dataGrid.operators.endsWith' }
];

/**
 * Maps negated text operators back to their base operator for the dropdown.
 * Derived from TEXT_OPERATORS: for each base op, `n{op}` → `{op}`.
 */
const NEGATED_TO_BASE: Record<string, TextFilterOperator> = Object.fromEntries(
  TEXT_OPERATORS.map((entry: { value: TextFilterOperator; label: string }): [string, TextFilterOperator] => [
    `n${entry.value}`,
    entry.value
  ])
);

/** Ordered list of numeric filter operators with display symbols. */
const NUMERIC_OPERATORS: ReadonlyArray<{ value: NumericFilterOperator; label: string }> = [
  { value: 'eq', label: 'components.dataGrid.operators.eq' },
  { value: 'neq', label: 'components.dataGrid.operators.neq' },
  { value: 'gt', label: 'components.dataGrid.operators.gt' },
  { value: 'gte', label: 'components.dataGrid.operators.gte' },
  { value: 'lt', label: 'components.dataGrid.operators.lt' },
  { value: 'lte', label: 'components.dataGrid.operators.lte' },
  { value: 'between', label: 'components.dataGrid.operators.between' }
];

/** Ordered list of date filter operators with display labels. */
const DATE_OPERATORS: ReadonlyArray<{ value: DateFilterOperator; label: string }> = [
  { value: 'eq', label: 'components.dataGrid.operators.exactly' },
  { value: 'after', label: 'components.dataGrid.operators.after' },
  { value: 'before', label: 'components.dataGrid.operators.before' }
];

/**
 * Column filter menu popup rendered as a floating card anchored below
 * a column header cell. Contains type-appropriate filter controls with
 * Apply/Clear actions.
 *
 * The menu uses local state for operator/value so the filter is only
 * emitted when the user clicks "Apply" — not while typing.
 */
@Component({
  selector: 'd-column-filter-menu',
  imports: [MatIconModule, NgStyle, TranslocoPipe],
  templateUrl: './column-filter-menu.component.html',
  styleUrl: './column-filter-menu.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  encapsulation: ViewEncapsulation.None,
  host: { '(document:keydown.escape)': 'onEscapeKey()' }
})
export class ColumnFilterMenuComponent {
  // ── Inputs ──────────────────────────────────────────────────────────

  /** The column definition for the filter being edited. */
  public column: InputSignal<DataGridColumn> = input.required<DataGridColumn>();

  /** The currently active filter for this column, or null if none. */
  public currentFilter: InputSignal<ColumnFilter | null> = input<ColumnFilter | null>(null);

  /** The anchor <th> element for positioning the popup. */
  public anchorElement: InputSignal<HTMLElement | null> = input<HTMLElement | null>(null);

  // ── Outputs ─────────────────────────────────────────────────────────

  /** Emits the new filter to apply (or null to clear). */
  public filterApply: OutputEmitterRef<{ field: string; filter: ColumnFilter | null }> = output<{
    field: string;
    filter: ColumnFilter | null;
  }>();

  /** Emits when the menu should close (backdrop click, Escape, etc). */
  public menuClose: OutputEmitterRef<void> = output<void>();

  // ── Local state ─────────────────────────────────────────────────────

  /** Local text operator state. */
  protected textOperator: WritableSignal<TextFilterOperator> = signal<TextFilterOperator>('contains');

  /** Whether the text filter is negated (e.g. "Does not contain"). */
  protected textNegate: WritableSignal<boolean> = signal<boolean>(false);

  /** Local text value state. */
  protected textValue: WritableSignal<string> = signal<string>('');

  /** Local numeric operator state. */
  protected numericOperator: WritableSignal<NumericFilterOperator> = signal<NumericFilterOperator>('eq');

  /** Local numeric value state. */
  protected numericValue: WritableSignal<string> = signal<string>('');

  /** Local numeric upper-bound value state (used when operator is 'between'). */
  protected numericValueTo: WritableSignal<string> = signal<string>('');

  /** Local date operator state. */
  protected dateOperator: WritableSignal<DateFilterOperator> = signal<DateFilterOperator>('eq');

  /** Local date value state. */
  protected dateValue: WritableSignal<string> = signal<string>('');

  /** Local boolean operator state. */
  protected booleanOperator: WritableSignal<'all' | 'true' | 'false'> = signal<'all' | 'true' | 'false'>(
    'all'
  );

  /** Expose operator lists to the template. */
  protected readonly textOperators: ReadonlyArray<{ value: TextFilterOperator; label: string }> =
    TEXT_OPERATORS;
  protected readonly numericOperators: ReadonlyArray<{ value: NumericFilterOperator; label: string }> =
    NUMERIC_OPERATORS;
  protected readonly dateOperators: ReadonlyArray<{ value: DateFilterOperator; label: string }> =
    DATE_OPERATORS;

  /** Computed popup position based on anchor element, clamped to the viewport. */
  protected popupStyle: Signal<Record<string, string>> = computed((): Record<string, string> => {
    const anchor: HTMLElement | null = this.anchorElement();
    if (anchor === null) {
      return {};
    }
    const rect: DOMRect = anchor.getBoundingClientRect();
    const popupWidth: number = Math.min(POPUP_MAX_WIDTH_PX, Math.max(rect.width, POPUP_MIN_WIDTH_PX));
    const viewportRight: number = window.innerWidth - POPUP_VIEWPORT_PADDING_PX;
    let left: number = rect.left;
    if (left + popupWidth > viewportRight) {
      left = rect.right - popupWidth;
    }
    const minLeft: number = POPUP_VIEWPORT_PADDING_PX;
    if (left < minLeft) {
      left = minLeft;
    }
    return {
      top: `${rect.bottom + POPUP_ANCHOR_GAP_PX}px`,
      left: `${left}px`,
      width: `${popupWidth}px`
    };
  });

  constructor() {
    // Local state is initialised from currentFilter in ngOnInit.
  }

  /** Initialise local state from the current filter when the menu opens. */
  public ngOnInit(): void {
    const filter: ColumnFilter | null = this.currentFilter();
    if (filter === null) {
      return;
    }
    switch (filter.type) {
      case 'text': {
        const baseOp: TextFilterOperator | undefined = NEGATED_TO_BASE[filter.operator];
        if (baseOp !== undefined) {
          this.textOperator.set(baseOp);
          this.textNegate.set(true);
        } else {
          this.textOperator.set(filter.operator);
          this.textNegate.set(false);
        }
        this.textValue.set(filter.value);
        break;
      }
      case 'numeric':
        this.numericOperator.set(filter.operator);
        this.numericValue.set(filter.value !== null ? String(filter.value) : '');
        if (filter.operator === 'between' && filter.valueTo !== undefined && filter.valueTo !== null) {
          this.numericValueTo.set(String(filter.valueTo));
        }
        break;
      case 'date':
        this.dateOperator.set(filter.operator);
        this.dateValue.set(filter.value ?? '');
        break;
      case 'boolean':
        this.booleanOperator.set(filter.operator);
        break;
    }
  }

  /** Close on Escape key. */
  protected onEscapeKey(): void {
    this.menuClose.emit();
  }

  // ── Actions ─────────────────────────────────────────────────────────

  /** Applies the current local filter state and closes the menu. */
  protected onApply(): void {
    const col: DataGridColumn = this.column();
    let filter: ColumnFilter | null = null;

    switch (col.type) {
      case 'text': {
        const value: string = this.textValue().trim();
        if (value.length > 0) {
          let operator: TextFilterOperator = this.textOperator();
          if (this.textNegate()) {
            operator = `n${operator}` as TextFilterOperator;
          }
          filter = { type: 'text', operator, value };
        }
        break;
      }
      case 'numeric': {
        const numValue: number = parseFloat(this.numericValue());
        if (!isNaN(numValue)) {
          if (this.numericOperator() === 'between') {
            const numValueTo: number = parseFloat(this.numericValueTo());
            if (!isNaN(numValueTo)) {
              filter = { type: 'numeric', operator: 'between', value: numValue, valueTo: numValueTo };
            }
          } else {
            filter = { type: 'numeric', operator: this.numericOperator(), value: numValue };
          }
        }
        break;
      }
      case 'date': {
        const dateValue: string = this.dateValue();
        if (dateValue.length > 0) {
          filter = { type: 'date', operator: this.dateOperator(), value: dateValue };
        }
        break;
      }
      case 'boolean': {
        const op: 'all' | 'true' | 'false' = this.booleanOperator();
        if (op !== 'all') {
          filter = { type: 'boolean', operator: op };
        }
        break;
      }
    }

    this.filterApply.emit({ field: col.field, filter });
    this.menuClose.emit();
  }

  /** Clears the filter and closes the menu. */
  protected onClear(): void {
    this.filterApply.emit({ field: this.column().field, filter: null });
    this.menuClose.emit();
  }

  /** Whether the local state has a meaningful filter value to apply. */
  protected hasValue(): boolean {
    switch (this.column().type) {
      case 'text':
        return this.textValue().trim().length > 0;
      case 'numeric':
        if (this.numericOperator() === 'between') {
          return (
            this.numericValue().trim().length > 0 &&
            !isNaN(parseFloat(this.numericValue())) &&
            this.numericValueTo().trim().length > 0 &&
            !isNaN(parseFloat(this.numericValueTo()))
          );
        }
        return this.numericValue().trim().length > 0 && !isNaN(parseFloat(this.numericValue()));
      case 'date':
        return this.dateValue().length > 0;
      case 'boolean':
        return this.booleanOperator() !== 'all';
      default:
        return false;
    }
  }

  // ── Template helpers ────────────────────────────────────────────────

  /**
   * Extracts the string value from an input/select change event.
   *
   * @param event - The DOM event from an `<input>` or `<select>`.
   * @returns The element's current value.
   */
  protected inputValue(event: Event): string {
    return (event.target as HTMLInputElement).value;
  }

  /**
   * Extracts a `TextFilterOperator` from a select change event.
   *
   * @param event - The DOM event from the text operator `<select>`.
   * @returns The selected text filter operator.
   */
  protected selectTextOperator(event: Event): TextFilterOperator {
    return (event.target as HTMLSelectElement).value as TextFilterOperator;
  }

  /**
   * Extracts a `NumericFilterOperator` from a select change event.
   *
   * @param event - The DOM event from the numeric operator `<select>`.
   * @returns The selected numeric filter operator.
   */
  protected selectNumericOperator(event: Event): NumericFilterOperator {
    return (event.target as HTMLSelectElement).value as NumericFilterOperator;
  }

  /**
   * Extracts the checked state from a checkbox change event.
   *
   * @param event - The DOM event from a checkbox `<input>`.
   * @returns Whether the checkbox is checked.
   */
  protected checkboxChecked(event: Event): boolean {
    return (event.target as HTMLInputElement).checked;
  }
}
